import api from './api';

/**
 * 일반 팝업 기제의 **admin 쪽** — 판정 조회 · 「확인」 기록
 * (줄기 `popup-and-announcement-surface` P2 · 결정 2026-10-15 §B).
 *
 * ⚠️ **서버는 이미 이 라우트를 슈퍼관리자에게 열어 두고 있었다 — 없던 것은 화면뿐이다.**
 * `popups-viewer.controller.ts`가 전역 `JwtAuthGuard`만 거치고 `viewerIdentity()`가
 * `type === 'superAdmin'`이면 `ADMIN` surface로 판정하며, 「봤음」은 `PopupView.superAdminId`에
 * 들어간다(칸이 둘인 근거는 `popups.service.ts`의 `viewerKey` JSDoc). 그래서 이 파일이 하는 일은
 * **그 라우트를 admin api 인스턴스로 부르는 것**뿐이고 서버 변경은 0줄이다.
 *
 * ⚠️ **base가 `/admin`이 아니다.** 그 컨트롤러는 `@Controller('popups')`이므로 경로가
 * `/api/v1/popups`이고, admin api의 `baseURL`이 이미 `/api/v1`이라 web과 **같은 상대 경로**를 쓴다.
 * `/admin/popups`로 적지 말 것 — 404가 된다.
 *
 * ⚠️ **조직 식별자·플랫폼을 보내지 않는다.** 그 컨트롤러가 스코프·surface·역할을 전부 토큰에서
 * 파생시키는 것이 설계다(받으면 web 사용자가 `ADMIN`을 주장할 수 있다 — 그 docblock이 정본).
 * 여기 쿼리를 더하면 그 파생점이 둘이 된다.
 */

/**
 * `GET /popups/active` 한 건 — 서버 `ActivePopupView`에서 **화면이 쓰는 칸만** 적는다.
 *
 * ⚠️ web 쪽 짝(`apps/web/src/lib/popups.ts`의 `ActivePopup`)과 **일부러 합치지 않았다** — 합치려면
 * `packages/shared`로 올려야 하고, 그러면 선언 하나가 두 앱의 빌드에 걸리는데 **얻는 것이 없다**
 * (양쪽이 서버 응답을 각자 좁게 읽는 것이 지금 관례다). 공유해야 하는 것은 **렌더 계약**이고
 * 그것은 이미 `packages/ui`의 `PopupHostItem`이 소유한다.
 *
 * ⚠️ **2026-10-29 — `frequency`·`optOut`을 담기 시작했다**(종전 「`frequency`를 담지 않는다 — 두
 * 버튼은 늘 보인다」의 전제가 뒤집혔다 · 결정 ⓒ·개정 Q1~Q4). 확인 문구와 opt-out 버튼의 종류가 그
 * 값으로 갈린다. **`string`으로 둔다** — 손으로 열거하면 Prisma enum의 사본이 되어 낡는다(해석은
 * 공용 호스트가 한다). 다시 뜰지는 여전히 **서버가 판정한다**.
 *
 * ⛔ `linkLabel`·`linkUrl`·`sourceKey`를 **여기 적지 말 것** — DB에는 있지만
 * `popups.service.ts#listActive`의 `.map`이 **내려주지 않는다**(2026-10-26 확인). 선언만 더하면
 * 늘 `undefined`인 칸이 생겨 「링크가 있다」로 오독된다. 그 경위와 목적지 소유 결정(ⓐ)은
 * `packages/ui`의 `PopupHost` JSDoc이 정본이다.
 */
export interface AdminActivePopup {
  id: string;
  /** null = 전사. admin은 전사 공지와 특정 조직 겨냥 공지를 모두 받을 수 있다 */
  organizationId: string | null;
  title: string;
  body: string;
  /** 재노출 — 확인 버튼 문구(`ONCE` 「읽었습니다」 · `EVERY_VISIT` 「닫기」) */
  frequency: string;
  /** opt-out — 버튼 종류(`NONE` 없음 · `HIDE_TODAY` 오늘 하루 · `NEVER_AGAIN` 다시 보지 않기) */
  optOut: string;
}

/**
 * 응답 봉투 — ⚠️ **admin `lib/api.ts`의 `ApiResponse`가 `export`되어 있지 않아** 여기 좁게 다시
 * 적는다(web은 `export type ApiResponse`라 그대로 import한다 — 두 앱의 비대칭이다).
 * `api.ts`를 고쳐 export를 넓히는 쪽은 **이 작업 범위가 아니다**(5,300줄 공용 모듈의 공개면을
 * 건드리는 일이라 영향 범위가 이 기능보다 크다). 필요한 칸은 `data`뿐이다.
 */
interface PopupsEnvelope<T> {
  data: T | null;
}

export async function fetchActivePopups(): Promise<AdminActivePopup[]> {
  const res = await api.get<PopupsEnvelope<AdminActivePopup[]>>('/popups/active');
  return res.data.data ?? [];
}

/**
 * 「확인」(닫기) — 서버에 봤음을 남긴다. **계정 축**이라 기기가 바뀌어도 유지되고, 슈퍼관리자는
 * 제 칸(`PopupView.superAdminId`)에 기록된다.
 */
export async function markPopupSeen(popupId: string): Promise<void> {
  await api.post(`/popups/${popupId}/seen`);
}

/**
 * 「다시 보지 않기」 — 계정 축(슈퍼관리자는 제 칸 `PopupView.superAdminId`). web 짝은
 * `apps/web/src/lib/popups.ts`의 `dismissPopup`이고 body를 `{}`로 보내는 이유도 같다.
 */
export async function dismissPopup(popupId: string): Promise<void> {
  await api.post(`/popups/${popupId}/dismiss`, {});
}
